import * as React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinearProgressWithLabel from "./LinearProgressWIthLabel";
import { useTranslation } from "react-i18next";
import {
  fetchAllCustomers,
  fetchAssessmentEvaluations,
  fetchEvaluationModuleDataByEvaluation,
  fetchModuleById,
  fetchQuestionById,
} from "../helpers/apiRequests";
import { CGI_BLACK, CGI_GREY, CGI_PURPLE, CGI_RED } from "../helpers/enums";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function getRatingRatio(data) {
  if (!data || !data.maxRating) return 0;
  return data.rating / data.maxRating;
}

function getQuestionProgress(questionRating) {
  if (!questionRating || !questionRating.maxRating) return 0;
  return (questionRating.rating / questionRating.maxRating) * 100;
}

export default function BenchmarkResults({
  selectedModule,
  selectedEvaluation,
  setNotificationMessage,
  setOpenNotification,
  setOpenLoading,
  secondEvaluation,
  setSecondEvaluation,
}) {
  const { t } = useTranslation();

  const [resultData, setResultData] = React.useState([]);
  const [showRadar, setShowRadar] = React.useState(true);
  const [selectedDisplayModule, setSelectedDisplayModule] = React.useState("");
  const [secondEvaluationOptions, setSecondEvaluationOptions] = React.useState(
    [],
  );

  async function enrichEvaluationModuleData(evaluationModuleData) {
    const sortedModuleData = (evaluationModuleData || []).slice().sort((a, b) =>
      (a.moduleID || "").localeCompare(b.moduleID || ""),
    );

    return await Promise.all(
      sortedModuleData.map(async (data, index) => {
        const module = data.moduleID ? await fetchModuleById(data.moduleID) : null;
        const questionRating = await Promise.all(
          (data.questionRating || []).map(async (rating) => {
            const questionData = rating.questionID
              ? await fetchQuestionById(rating.questionID)
              : null;

            return { ...rating, questionData };
          }),
        );

        return {
          ...data,
          index: module?.sortingNumber ?? index + 1,
          name: module?.title || data.moduleID,
          sortingNumber: module?.sortingNumber ?? index + 1,
          questionRating,
        };
      }),
    );
  }

  async function fetchEvaluationData() {
    if (secondEvaluation === "") {
      return;
    }

    setOpenLoading(true);
    try {
      const [selectedData, comparedData] = await Promise.all([
        fetchEvaluationModuleDataByEvaluation(selectedEvaluation.id),
        fetchEvaluationModuleDataByEvaluation(secondEvaluation.id),
      ]);

      const [selectedModules, comparedModules] = await Promise.all([
        enrichEvaluationModuleData(selectedData),
        enrichEvaluationModuleData(comparedData),
      ]);

      const comparedByModule = new Map(
        comparedModules.map((moduleData) => [moduleData.moduleID, moduleData]),
      );

      const completeData = selectedModules.map((moduleData, index) => {
        const comparedModule = comparedByModule.get(moduleData.moduleID);
        const comparedQuestions = new Map(
          (comparedModule?.questionRating || []).map((question) => [
            question.questionID,
            question,
          ]),
        );

        return {
          ...moduleData,
          index: moduleData.sortingNumber ?? index + 1,
          totalRatingInPercent: getRatingRatio(moduleData),
          secondEvaluationRatingInPercent: getRatingRatio(comparedModule),
          questionsData: (moduleData.questionRating || []).map(
            (question, questionIndex) => ({
              ...question,
              index: questionIndex + 1,
              questionId: question.questionID,
              content:
                question.questionData?.text ||
                question.questionData?.title ||
                question.questionID,
              rating: getQuestionProgress(question),
              secondRating: getQuestionProgress(
                comparedQuestions.get(question.questionID),
              ),
            }),
          ),
        };
      });

      setResultData(completeData);
      setSelectedDisplayModule("");
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorFetchDataMessage"));
      setOpenNotification(true);
    }
    setOpenLoading(false);
  }

  async function getSecondEvaluationOptions() {
    if (!selectedModule?.id) return;

    const [assessmentEvaluations, ownCustomers] = await Promise.all([
      fetchAssessmentEvaluations(selectedModule.id),
      fetchAllCustomers(true),
    ]);

    const customerById = new Map(
      (ownCustomers || []).map((customer) => [customer.id, customer]),
    );

    const ownEvaluations = assessmentEvaluations.filter(
      (evaluation) =>
        evaluation.id !== selectedEvaluation.id &&
        evaluation.state === "FINISHED" &&
        customerById.has(evaluation.customerID),
    );

    const groupedOptions = [];
    let lastCustomerId = "";
    ownEvaluations
      .sort((a, b) => {
        const customerCompare = (customerById.get(a.customerID)?.name || "")
          .localeCompare(customerById.get(b.customerID)?.name || "");
        if (customerCompare !== 0) return customerCompare;
        return new Date(b.createdAt) - new Date(a.createdAt);
      })
      .forEach((evaluation) => {
        if (evaluation.customerID !== lastCustomerId) {
          lastCustomerId = evaluation.customerID;
          groupedOptions.push({
            headingElement: true,
            heading: customerById.get(evaluation.customerID)?.name || "",
          });
        }
        groupedOptions.push(evaluation);
      });

    setSecondEvaluationOptions(groupedOptions);
  }

  React.useEffect(() => {
    getSecondEvaluationOptions();
  }, [selectedModule?.id, selectedEvaluation?.id]);

  React.useEffect(() => {
    fetchEvaluationData();
  }, [secondEvaluation]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        mt: 5,
        mx: { xs: 0.5, lg: 5 },
      }}
    >
      <Typography
        sx={{
          fontSize: 20,
          fontWeight: 500,
          color: CGI_RED,
          borderBottom: 1,
          borderColor: CGI_RED,
          mb: 1,
          pb: 1,
        }}
      >
        CGI Compass: {t("benchmarkToEvaluation")} "{selectedEvaluation.id}".
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: 1,
          justifyContent: "flex-end",
          alignItems: "center",
          mr: 3,
        }}
      >
        <Typography sx={{ fontSize: 15, mr: 1.5 }}>
          {t("selectEvaluationToCompareTo")}
        </Typography>
        <Select
          value={secondEvaluation}
          onChange={(event) => {
            setSecondEvaluation(event.target.value);
          }}
          inputProps={{ "aria-label": "Without label" }}
          size="small"
          MenuProps={{
            PaperProps: {
              sx: {
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "0.4em",
                },
                "&::-webkit-scrollbar-track": {
                  width: "0.6em",
                  boxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
                  webkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: CGI_BLACK,
                  borderRadius: 20,
                },
              },
            },
          }}
          sx={{
            fontSize: 20,
            height: 30,
            boxShadow: "none",
            ".MuiOutlinedInput-notchedOutline": {
              borderRadius: 5,
              border: 1,
              borderColor: "#777",
            },
            "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderRadius: 5,
            },
            "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderRadius: 5,
              },
          }}
          displayEmpty
        >
          <MenuItem value="">{t("noSelection")}</MenuItem>
          {secondEvaluationOptions.map((option, index) => (
            <MenuItem
              disabled={option.headingElement}
              key={index}
              value={option}
              sx={
                option.headingElement
                  ? {
                      color: CGI_RED,
                      textDecoration: "underline",
                      opacity: "1 !important",
                      fontWeight: 500,
                      borderTop: 1,
                    }
                  : {}
              }
            >
              {option.headingElement
                ? t("customer") + " " + option.heading
                : t("evaluationFrom") +
                  " " +
                  new Date(option.createdAt).toLocaleString(t("timeSettings"), {
                    timeZone: "Europe/Berlin",
                  })}
            </MenuItem>
          ))}
        </Select>
      </Box>
      {secondEvaluation === "" ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            width: 1,
            mt: 15,
          }}
        >
          <Typography
            sx={{
              fontSize: 23,
              color: CGI_BLACK,
            }}
          >
            {t("pleaseSelectEvaluationToCompareTo")}
          </Typography>
        </Box>
      ) : (
        <React.Fragment>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              width: 1,
              justifyContent: "flex-end",
              mt: 3,
              mr: 3,
            }}
          >
            <Button
              variant="outlined"
              sx={{
                borderRadius: 20,
                fontWeight: 500,
                textTransform: "none",
              }}
              onClick={() => setShowRadar(!showRadar)}
            >
              {showRadar ? "Bar Chart" : "Radar Chart"}
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              height: 600,
            }}
          >
            <ResponsiveContainer>
              {showRadar ? (
                <RadarChart
                  id="radarChartId"
                  cx="50%"
                  cy="50%"
                  outerRadius="90%"
                  data={resultData}
                >
                  <PolarGrid />
                  <PolarAngleAxis dataKey="index" />
                  <Radar
                    name={"Assessment 1"}
                    dataKey="totalRatingInPercent"
                    stroke={CGI_RED}
                    fill={CGI_RED}
                    fillOpacity={0.6}
                  />
                  <Radar
                    name={"Assessment 2"}
                    dataKey="secondEvaluationRatingInPercent"
                    stroke={CGI_PURPLE}
                    fill={CGI_PURPLE}
                    fillOpacity={0.4}
                  />
                  <Legend />
                </RadarChart>
              ) : (
                <BarChart
                  width={500}
                  height={300}
                  data={resultData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="index" />
                  <YAxis domain={[0, 1]} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    name={"Assessment 1"}
                    dataKey="totalRatingInPercent"
                    stackId="a"
                    fill={CGI_RED}
                  />
                  <Bar
                    name={"Assessment 2"}
                    dataKey="secondEvaluationRatingInPercent"
                    stackId="a"
                    fill={CGI_PURPLE}
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
          </Box>
          <Divider
            sx={{
              mb: 3,
              mt: 10,
              userSelect: "none",
              bgcolor: "transparent",
              borderColor: "white",
              "&::before, &::after": {
                borderColor: CGI_BLACK,
              },
            }}
            textAlign="left"
          >
            <Typography
              sx={{ fontWeight: 500, fontSize: 20, fontStyle: "italic", pr: 1 }}
            >
              {t("module")}
            </Typography>
          </Divider>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              maxWidth: 1000,
              alignSelf: "center",
            }}
          >
            <Divider sx={{ my: 1 }} />
            {resultData.map((moduleData, index) => (
              <React.Fragment key={index}>
                <Box
                  onClick={() => {
                    setSelectedDisplayModule(moduleData);
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor:
                      selectedDisplayModule === moduleData ? "#22222211" : "",
                    "&:hover": {
                      cursor: "pointer",
                      bgcolor: "#22222211",
                    },
                    p: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 19,
                      fontWeight: 500,
                      pr: 2,
                      userSelect: "none",
                    }}
                  >
                    {moduleData.index + ")"}
                  </Typography>
                  <Typography
                    sx={{
                      mb: 0.2,
                      fontSize: { xs: 15, md: 18 },
                      minWidth: 1 / 3,
                      maxWidth: 1 / 3,
                      textAlign: "center",
                      userSelect: "none",
                    }}
                  >
                    {moduleData.name}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <Divider
                      flexItem
                      orientation="vertical"
                      sx={{ ml: 2, mr: 1 }}
                    />
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            userSelect: "none",
                            minWidth: 85,
                          }}
                        >
                          Assessment 1
                        </Typography>
                        <LinearProgressWithLabel
                          progressValue={moduleData.totalRatingInPercent * 100}
                        />
                      </Box>
                      <Divider />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          width: 1,
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            userSelect: "none",
                            minWidth: 85,
                          }}
                        >
                          Assessment 2
                        </Typography>
                        <LinearProgressWithLabel
                          progressValue={
                            moduleData.secondEvaluationRatingInPercent * 100
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
              </React.Fragment>
            ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              maxWidth: 1400,
              alignSelf: "center",
            }}
          >
            {selectedDisplayModule !== "" && (
              <Divider
                sx={{
                  mb: 2,
                  mt: 10,
                  userSelect: "none",
                  bgcolor: "transparent",
                  "&::before, &::after": {
                    borderColor: CGI_BLACK,
                  },
                }}
                textAlign="left"
              >
                <Typography
                  sx={{ fontWeight: 500, fontSize: 20, fontStyle: "italic" }}
                >
                  {t("questions")}
                </Typography>
              </Divider>
            )}
            {selectedDisplayModule !== "" &&
              (selectedDisplayModule.questionsData || []).map(
                (questionData, i) => (
                  <Accordion
                    sx={{
                      border: 1,
                      borderRadius: "20px !important",
                      borderColor: CGI_GREY,
                    }}
                    key={questionData.questionId || i}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 500,
                            minWidth: 100,
                          }}
                        >
                          {t("question")} {selectedDisplayModule.index}.{i + 1}
                        </Typography>
                        <Typography
                          sx={{
                            width: 1,
                            fontFamily: "Inter",
                            fontSize: { xs: 13, md: 14, lg: 15 },
                          }}
                        >
                          {questionData.content}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            mx: 2,
                            minWidth: 325,
                            maxWidth: 325,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              width: 1,
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 10,
                                userSelect: "none",
                                minWidth: 60,
                              }}
                            >
                              Assessment 1
                            </Typography>
                            <LinearProgressWithLabel
                              progressValue={questionData.rating}
                            />
                          </Box>
                          <Divider />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              width: 1,
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 10,
                                userSelect: "none",
                                minWidth: 60,
                              }}
                            >
                              Assessment 2
                            </Typography>
                            <LinearProgressWithLabel
                              progressValue={questionData.secondRating}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{ borderTop: 1, borderColor: "#bbb" }}
                    >
                      <Typography
                        sx={{
                          mt: 0.7,
                          ml: 1,
                          fontSize: { xs: 13, md: 14, lg: 15 },
                        }}
                      >
                        {t("source")}: {questionData.questionData?.source || "n/a"}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ),
              )}
          </Box>
        </React.Fragment>
      )}
    </Box>
  );
}
